import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import Layout from '../../components/ui/Layout';
import Table from '../../components/ui/Table';
import Modal from '../../components/ui/Modal';
import ValidatedInput from '../../components/ui/ValidatedInput';
import ValidatedTextarea from '../../components/ui/ValidatedTextarea';
import codeGeneratorService from '../../services/codeGeneratorService';
// @ts-ignore - ActionDropdown component exists but may have type issues
import ActionDropdown from '../../components/ui/ActionDropdown';
import ActionButtonsRow from '../../components/ui/ActionButtonsRow';
import SearchFilterCard, { FilterConfig } from '../../components/ui/SearchFilterCard';
import BulkActionsBar from '../../components/ui/BulkActionsBar';
import { RiEditLine, RiDeleteBinLine, RiEyeLine, RiDeleteBin2Line, RiFileDownloadLine, RiErrorWarningLine, RiCpuLine, RiInformationLine, RiHistoryLine } from 'react-icons/ri';
import deviceModelService, { DeviceModel, CreateDeviceModelRequest, UpdateDeviceModelRequest } from '../../services/deviceModelService';
import brandService, { Brand } from '../../services/brandService';
import { showAlert, showConfirm } from '../../utils/alert';
import { mapFieldErrors, focusFirstInvalidField, getApiErrorMessageAsync } from '../../utils/mapFieldErrors';
import { queryKeys } from '../../queryClient';
import { PAGINATION_CONFIG } from '../../config/pagination';
import Pagination from '../../components/ui/Pagination';
import { useUserPageSize } from '../../hooks/useUserPageSize';
import { formatDateTimeFull, getLocalDateString } from '../../utils/dateUtils';
import { downloadExcelFile } from '../../utils/downloadExcel';
import { CanCreate, CanEdit, CanDelete } from '../../components/auth/PermissionGuard';
import { usePermission } from '../../hooks/usePermission';
import LoadingOverlayModal from '../../components/ui/LoadingOverlayModal';
import { useClampPage } from '../../hooks/useClampPage';
import { withCurrentOption } from '../../utils/withCurrentOption';
import { runBulkDelete, bulkDeleteFailureMessage } from '../../utils/bulkDelete';

const DeviceModelPage: React.FC = () => {
  const { canView, canCreate, canEdit, canDelete, canExport } = usePermission();
  // S1118 URL-sync: read URL params for lazy init (filter+page bidirectional)
  const [searchParams, setSearchParams] = useSearchParams();
  const urlPageParam = searchParams.get('page');
  const urlBrandParam = searchParams.get('brand') || '';
  const urlStatusParam = searchParams.get('status') || '';

  // State สำหรับการค้นหาและ pagination
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(() => {
    if (!urlPageParam) return 1;
    const n = parseInt(urlPageParam, 10);
    if (!Number.isFinite(n) || n < 1) return 1;
    return Math.min(n, 9999);
  });
  const [pageSize, setPageSize] = useState<number>(PAGINATION_CONFIG.DEFAULT_PAGE_SIZE);
  useUserPageSize(pageSize, setPageSize);
  // S1118 URL-sync: hydrate filters from URL (brand is numeric ID · status maps to boolean)
  const [filterBrandId, setFilterBrandId] = useState<number | undefined>(() => {
    if (!urlBrandParam) return undefined;
    const n = parseInt(urlBrandParam, 10);
    if (!Number.isFinite(n) || n < 1) return undefined;
    return n;
  });
  const [filterIsActive, setFilterIsActive] = useState<boolean | undefined>(() => {
    if (urlStatusParam === 'active') return true;
    if (urlStatusParam === 'inactive') return false;
    return undefined;
  });

  // S1118 URL-sync: write filter+page to URL (Variant A overwrite + bidirectional status mapper)
  const syncFiltersToUrl = useCallback((brandId: number | undefined, isActive: boolean | undefined, page: number) => {
    const newParams = new URLSearchParams();
    if (brandId !== undefined && brandId > 0) newParams.set('brand', String(brandId));
    if (isActive === true) newParams.set('status', 'active');
    else if (isActive === false) newParams.set('status', 'inactive');
    if (page > 1) newParams.set('page', String(page));
    setSearchParams(newParams, { replace: true });
  }, [setSearchParams]);

  useEffect(() => {
    syncFiltersToUrl(filterBrandId, filterIsActive, currentPage);
  }, [filterBrandId, filterIsActive, currentPage, syncFiltersToUrl]);

  // State สำหรับควบคุมการแสดง success message
  const [shouldShowSuccess, setShouldShowSuccess] = useState(true);

  // State สำหรับ Modal
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedModel, setSelectedModel] = useState<DeviceModel | null>(null);
  const [modalMode, setModalMode] = useState<'create' | 'edit' | 'view'>('create');

  // State สำหรับฟอร์ม
  const [formData, setFormData] = useState<CreateDeviceModelRequest>({
    code: '',
    name: '',
    brand_id: 0,
    description: '',
    is_active: true,
  });

  // Simple validation state
  const [formErrors, setFormErrors] = useState<{[key: string]: string}>({});
  const [codeError, setCodeError] = useState<string>('');
  const [selectedModels, setSelectedModels] = useState<number[]>([]);
  const [bulkLoading, setBulkLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  // React Query for data fetching
  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ['device-models', { page: currentPage, limit: pageSize, search: searchTerm, brand_id: filterBrandId, is_active: filterIsActive }],
    queryFn: async () => {
      const params = {
        page: currentPage,
        limit: pageSize,
        search: searchTerm || undefined,
        brand_id: filterBrandId,
        is_active: filterIsActive,
      };
      return await deviceModelService.getAll(params);
    },
  });

  // Query สำหรับดึงข้อมูลยี่ห้อที่ active
  const { data: activeBrands = [] } = useQuery({
    queryKey: queryKeys.brands.active(),
    queryFn: () => brandService.getActiveBrands(),
    enabled: canView('brands'), // KD3 S2470: ไม่มีสิทธิ์ = ไม่ยิง (เดิม 403 + console error)
  });

  // KD3 S2492 (NEG-MD-020): ตัวกรองเดิมมีแต่ยี่ห้อที่เปิดใช้งาน แต่ backend กรอง brand_id ไม่สนสถานะ
  // → ปิดยี่ห้อแล้ว ?brand=<id> ยังกรองตารางอยู่แต่ช่องตัวกรองว่าง (จอไม่บอกว่ากรองอะไร) และเลือกหารุ่นของยี่ห้อนั้นไม่ได้อีก
  // ทั้งที่รุ่นยังอยู่ครบ · เติมยี่ห้อที่ปิดใช้งานต่อท้ายพร้อมป้าย
  const { data: inactiveBrands = [] } = useQuery({
    queryKey: queryKeys.brands.inactive(),
    queryFn: async () => (await brandService.getBrands({ is_active: false, limit: 100 })).items || [],
    enabled: canView('brands'),
  });

  // ข้อมูลรุ่นอุปกรณ์จาก React Query
  const models = data?.items || [];
  const totalItems = data?.total || 0;
  useClampPage(currentPage, data?.total, pageSize, setCurrentPage, isLoading);

  // ฟังก์ชันสำหรับการเลือกรุ่นอุปกรณ์ทั้งหมด
  // KD3 S2515: เปลี่ยนตัวกรอง/หน้าแล้วต้องล้างการเลือก — ไม่งั้นแถบ "เลือกแล้ว N" ค้าง และ "ลบที่เลือก" ลบแถวที่ผู้ใช้มองไม่เห็นแล้ว
  useEffect(() => {
    setSelectedModels([]);
  }, [currentPage, pageSize, searchTerm, filterBrandId, filterIsActive]);

  const handleSelectAll = (selected: boolean) => {
    if (selected) {
      setSelectedModels(models.map(model => model.id));
    } else {
      setSelectedModels([]);
    }
  };

  // ฟังก์ชันสำหรับการเลือกรุ่นอุปกรณ์แต่ละรายการ
  const handleSelectModel = (modelId: number, selected: boolean) => {
    if (selected) {
      setSelectedModels(prev => [...prev, modelId]);
    } else {
      setSelectedModels(prev => prev.filter(id => id !== modelId));
    }
  };

  // ฟังก์ชันสำหรับการลบรุ่นอุปกรณ์หลายรายการ
  const handleBulkDelete = async () => {
    if (selectedModels.length === 0) {
      showAlert('เลือกข้อมูล', 'กรุณาเลือกรุ่นอุปกรณ์ที่ต้องการลบ', 'info');
      return;
    }

    showConfirm(
      'ยืนยันการลบรุ่นอุปกรณ์',
      `คุณต้องการลบรุ่นอุปกรณ์ ${selectedModels.length} รายการที่เลือก ใช่หรือไม่? การกระทำนี้ไม่สามารถเปลี่ยนกลับได้`,
      async () => {
        setBulkLoading(true);
        try {
          // BUG-S2103-002: allSettled — รายการที่ลบไม่ได้ต้องบอกเหตุผล · ตารางต้อง refetch · เลือกค้างเฉพาะที่ลบไม่ได้
          const result = await runBulkDelete(selectedModels, id => deviceModelService.delete(id));
          setSelectedModels(result.failed.map(f => f.id));
          refetch();
          if (result.failed.length === 0) {
            showAlert('สำเร็จ', `ลบรุ่นอุปกรณ์ ${result.succeeded.length} รายการสำเร็จ`, 'success');
          } else {
            showAlert(result.succeeded.length ? 'ลบได้บางรายการ' : 'ลบไม่สำเร็จ', bulkDeleteFailureMessage('รุ่นอุปกรณ์', result), result.succeeded.length ? 'warning' : 'error');
          }
        } catch (error) {
          console.error('Error bulk deleting:', error);
          showAlert('เกิดข้อผิดพลาด', 'ไม่สามารถลบรายการที่เลือกได้', 'error');
        } finally {
          setBulkLoading(false);
        }
      },
      { isDanger: true, confirmText: 'ลบ', cancelText: 'ยกเลิก' }
    );
  };

  // Table columns configuration
  const columns = [
    {
      key: 'select',
      title: (
        <div className="flex items-center">
          <input
            type="checkbox"
            aria-label="เลือกทั้งหมด"
            checked={selectedModels.length === models.length && models.length > 0}
            ref={(input) => { if (input) { input.indeterminate = selectedModels.length > 0 && selectedModels.length < models.length; } }}
            onChange={(e) => handleSelectAll(e.target.checked)}
            className="rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
          />
        </div>
      ),
      width: '50px',
      render: (value: any, record: DeviceModel) => (
        <input
          type="checkbox"
          aria-label={`เลือกรายการ ${record.code || record.id}`}
          checked={selectedModels.includes(record.id)}
          onChange={(e) => handleSelectModel(record.id, e.target.checked)}
          className="rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
        />
      ),
    },
    {
      key: 'code',
      title: 'รหัสรุ่น',
      render: (value: string) => <span className="font-mono text-sm">{value || '-'}</span>,
      sortable: true,
      width: '120px'
    },
    {
      key: 'name',
      title: 'ชื่อรุ่น',
      render: (value: string) => <span className="font-medium">{value || '-'}</span>,
      sortable: true
    },
    {
      key: 'brand',
      title: 'ยี่ห้อ',
      render: (value: any, record: DeviceModel) => (
        <span className="text-secondary-700">{record.brand?.name || '-'}</span>
      ),
      sortable: true,
      width: '150px'
    },
    {
      key: 'description',
      title: 'คำอธิบาย',
      render: (value: string) => <span className="text-gray-600">{value || '-'}</span>
    },
    {
      key: 'is_active',
      title: 'สถานะ',
      render: (value: boolean) => (
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
          value
            ? 'bg-success-100 text-success-800'
            : 'bg-danger-100 text-danger-800'
        }`}>
          {value ? 'ใช้งาน' : 'ไม่ใช้งาน'}
        </span>
      ),
      width: '100px'
    },
    {
      key: 'created_at',
      title: 'วันที่สร้าง',
      sortable: true,
      render: (value: string | null) => formatDateTimeFull(value)
    },
    {
      key: 'updated_at',
      title: 'อัพเดทล่าสุด',
      sortable: true,
      render: (value: string | null) => formatDateTimeFull(value)
    },
    {
      key: 'actions',
      title: 'การดำเนินการ',
      sticky: true,
      render: (_: any, record: DeviceModel) => (
        <ActionDropdown
          actions={[
            ...(canView('device_models') ? [{
              label: 'ดูรายละเอียด',
              icon: <RiEyeLine className="w-4 h-4" />,
              onClick: () => handleOpenModal('view', record)
            }] : []),
            ...(canEdit('device_models') ? [{
              label: 'แก้ไข',
              icon: <RiEditLine className="w-4 h-4" />,
              onClick: () => handleOpenModal('edit', record)
            }] : []),
            ...(canDelete('device_models') ? [{
              divider: true
            },
            {
              label: 'ลบ',
              icon: <RiDeleteBinLine className="w-4 h-4" />,
              onClick: () => handleDelete(record),
              className: 'text-danger-600 hover:text-danger-700 hover:bg-danger-50'
            }] : [])
          ]}
          buttonIcon={null}
          buttonLabel=""
        />
      ),
      width: '60px'
    }
  ];


  // จัดการการค้นหา
  const handleSearch = (term: string) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  // จัดการการเปลี่ยนหน้า
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // จัดการการเปิด Modal
  const handleOpenModal = async (mode: 'create' | 'edit' | 'view', model?: DeviceModel) => {
    setModalMode(mode);
    setSelectedModel(model || null);

    if (mode === 'create') {
      // ใช้ preview รหัสถัดไปแทนการสร้างรหัสจริง
      let previewCode = '';
      try {
        const nextCodePreview = await codeGeneratorService.getNextCodePreview('device_model', true);
        previewCode = nextCodePreview.next_code;
      } catch (error) {
        console.error('Error getting next code preview:', error);
        previewCode = ''; // fallback to empty
      }

      setFormData({
        code: previewCode,
        name: '',
        brand_id: 0,
        description: '',
        is_active: true,
      });
    } else if (model) {
      setFormData({
        code: model.code,
        name: model.name,
        brand_id: model.brand_id,
        description: model.description || '',
        is_active: model.is_active,
      });
    }

    setFormErrors({});
    setCodeError('');
    setShouldShowSuccess(true);
    setIsModalOpen(true);
  };

  // จัดการการปิด Modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedModel(null);
    setFormErrors({});
    setCodeError('');
    // Reset form data เมื่อปิด modal
    setFormData({
      code: '',
      name: '',
      brand_id: 0,
      description: '',
      is_active: true,
    });
  };

  // จัดการการเปลี่ยนแปลงข้อมูลในฟอร์ม
  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;

    // รหัสห้ามมีช่องว่าง — ตัดทิ้งตอนพิมพ์/วางจาก Excel/LINE ไม่งั้นโดนปฏิเสธทั้งที่ตาเห็นถูก (BUG-S2295-003)
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked :
              name === 'brand_id' ? parseInt(value) :
              (name === 'code' ? value.replace(/\s/g, '') : value)
    }));

    // Clear field error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // จัดการการบันทึกข้อมูล
  const handleSave = async () => {
    // Validate all fields
    const errors: {[key: string]: string} = {};

    if (!formData.code.trim()) {
      errors.code = 'กรุณาใส่รหัสรุ่น';
    } else if (formData.code.length < 2 || formData.code.length > 50) {
      // BUG-S1206-002 fix: align FE limit with BE Pydantic validator (≤50)
      // Code field is currently auto-generated (disabled), so this is a latent drift fix
      // — prevents silent submit failures if code-template setting produces >20 chars
      errors.code = 'รหัสรุ่นต้องมีความยาว 2-50 ตัวอักษร';
    } else if (!/^[A-Za-z0-9_-]+$/.test(formData.code)) {
      errors.code = 'รหัสรุ่นต้องเป็นตัวอักษร ตัวเลข _ หรือ - เท่านั้น';
    }

    if (!formData.brand_id || formData.brand_id === 0) {
      errors.brand_id = 'กรุณาเลือกยี่ห้อ';
    }

    if (!formData.name.trim()) {
      errors.name = 'กรุณาใส่ชื่อรุ่น';
    } else if (formData.name.length < 2 || formData.name.length > 200) {
      // BUG-S1204-001 fix: align FE limit with BE Pydantic validator (≤200) + DB varchar(200)
      errors.name = 'ชื่อรุ่นต้องมีความยาว 2-200 ตัวอักษร';
    }

    setFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      // Scroll to first error field
      const firstErrorField = Object.keys(errors)[0];
      const errorElement = document.querySelector(`[name="${firstErrorField}"]`);
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        (errorElement as HTMLInputElement).focus();
      }
      return;
    }

    // ตั้งค่าให้แสดง success message เป็นค่าเริ่มต้น
    setShouldShowSuccess(true);
    setIsSaving(true);

    try {
      if (modalMode === 'create') {
        await deviceModelService.create(formData);
        handleCloseModal();
        setCodeError('');

        // แสดง success message เฉพาะเมื่อไม่มีรหัสซ้ำ
        // KD3 S2121 (BUG-S2121-002): แสดงเสมอ — flag อ่านจาก closure ตอนกดบันทึก จึงค้าง false จากครั้งที่ล้มก่อนหน้า
        showAlert('สำเร็จ', 'สร้างรุ่นอุปกรณ์สำเร็จ', 'success');
        refetch();
      } else if (modalMode === 'edit' && selectedModel) {
        const updateData: UpdateDeviceModelRequest = {
          code: formData.code,
          name: formData.name,
          brand_id: formData.brand_id,
          description: formData.description,
          is_active: formData.is_active,
        };

        // BUG-S162-P1: send last-seen updated_at so a stale two-tab overwrite → 409 (not silent clobber)
        await deviceModelService.update(selectedModel.id, updateData, (selectedModel as any).updated_at || (selectedModel as any).created_at);
        handleCloseModal();
        setCodeError('');
        showAlert('สำเร็จ', 'แก้ไขรุ่นอุปกรณ์สำเร็จ', 'success');
        refetch();
      }
    } catch (error: any) {
      console.error('Error saving device model:', error);

      // BUG-S1162-001 PROPAGATION (S1165): server 422 field_errors → inline (WCAG 3.3.1)
      const fieldErrMap = mapFieldErrors(error);
      if (fieldErrMap) {
        setFormErrors(prev => ({ ...prev, ...fieldErrMap }));
        focusFirstInvalidField(fieldErrMap);
        setShouldShowSuccess(false);
        return;
      }

      // BUG-S162-P1: optimistic-lock conflict — another tab/user saved first. Tell the user
      // to reload (don't silently overwrite their changes) and refresh the list.
      if (error.response?.status === 409) {
        setShouldShowSuccess(false);
        showAlert('ข้อมูลถูกแก้ไขโดยผู้ใช้อื่น', 'มีผู้ใช้อื่นบันทึกข้อมูลนี้ไปแล้ว กรุณาปิดหน้าต่างนี้แล้วโหลดข้อมูลใหม่ก่อนแก้ไขอีกครั้ง', 'error');
        refetch();
        return;
      }

      // ตรวจสอบว่าเป็น error รหัสซ้ำหรือไม่
      const errorMessage = error.response?.data?.error?.user_message ||
                           error.response?.data?.error?.message ||
                           error.response?.data?.detail || '';

      if (errorMessage.includes('ชื่อ') && errorMessage.includes('มีอยู่ในระบบแล้ว')) {
        // OBS-S1109-001 FIX (S1113): BUSINESS_RULE_VIOLATION on name dup → inline field error
        setFormErrors(prev => ({ ...prev, name: errorMessage }));
        setShouldShowSuccess(false);
        const nameEl = document.getElementsByName('name')[0] as HTMLInputElement | undefined;
        if (nameEl) nameEl.focus();
      } else if (errorMessage.includes('รหัส') && errorMessage.includes('มีอยู่ในระบบแล้ว')) {
        // ถ้าเป็นรหัสซ้ำ ให้สร้างรหัสใหม่
        const oldCode = formData.code;
        const newCode = await codeGeneratorService.generateSingleCode('device_model');

        // อัพเดท state
        setFormData({...formData, code: newCode});

        // แสดงข้อความเตือน
        setCodeError(`รหัส ${oldCode} ไม่ว่างเนื่องจากมีการใช้งานแล้ว กรุณาบันทึกใหม่`);

        // ตั้งค่าให้ไม่แสดง success message
        setShouldShowSuccess(false);
      } else {
        // ถ้าไม่ใช่ error รหัสซ้ำ ให้แสดง alert ปกติ
        showAlert('เกิดข้อผิดพลาด', errorMessage || 'ไม่สามารถบันทึกรุ่นอุปกรณ์ได้', 'error');
      }
    } finally {
      setIsSaving(false);
    }
  };

  // จัดการการลบข้อมูล
  const handleDelete = (model: DeviceModel) => {
    showConfirm(
      'ยืนยันการลบ',
      `คุณต้องการลบรุ่นอุปกรณ์ "${model.name}" ใช่หรือไม่? การกระทำนี้ไม่สามารถเปลี่ยนกลับได้`,
      async () => {
        try {
          await deviceModelService.delete(model.id);
          showAlert('สำเร็จ', 'ลบรุ่นอุปกรณ์สำเร็จ', 'success');
          refetch();
        } catch (error: any) {
          console.error('Error deleting device model:', error);
          // BUG-S397-DELMSG: the delete-in-use guard returns a specific reason
          // ("ไม่สามารถลบได้ เนื่องจากมีอุปกรณ์ N รายการที่ใช้รุ่นนี้") in error.user_message, but this
          // catch hardcoded a generic "ไม่สามารถลบรุ่นอุปกรณ์ได้" that hid WHY the delete was blocked
          // (same swallow-family as BUG-S370/S374). Surface the backend user_message like handleSave does.
          const errorMessage = error.response?.data?.error?.user_message ||
                               error.response?.data?.error?.message ||
                               error.response?.data?.detail ||
                               'ไม่สามารถลบรุ่นอุปกรณ์ได้';
          showAlert('เกิดข้อผิดพลาด', errorMessage, 'error');
        }
      },
      { isDanger: true, confirmText: 'ลบ', cancelText: 'ยกเลิก' }
    );
  };

  // ฟังก์ชัน Refresh
  const handleRefresh = () => {
    setSearchTerm('');
    setCurrentPage(1);
    setFilterBrandId(undefined);
    setFilterIsActive(undefined);
    setSelectedModels([]);
    refetch();
  };

  // State สำหรับ Export Loading
  const [isExporting, setIsExporting] = useState(false);

  // ฟังก์ชัน Export Excel
  const handleExportExcel = async () => {
    setIsExporting(true);
    try {
      // ส่งตัวกรองชุดเดียวกับที่หน้ารายการใช้ — ไฟล์ต้องได้เฉพาะแถวที่เห็นบนจอ
      const blob = await deviceModelService.exportToExcel({
        search: searchTerm || undefined,
        brand_id: filterBrandId,
        is_active: filterIsActive,
      });
      downloadExcelFile(blob, `device_models_${getLocalDateString()}.xlsx`);
      showAlert('สำเร็จ', 'ส่งออกข้อมูลเรียบร้อยแล้ว', 'success');
    } catch (error) {
      console.error('Error exporting data:', error);
      showAlert('เกิดข้อผิดพลาด', await getApiErrorMessageAsync(error, 'ไม่สามารถส่งออกข้อมูลได้'), 'error');
    } finally {
      setIsExporting(false);
    }
  };

  // สร้าง filters สำหรับ SearchFilterCard
  const filters: FilterConfig[] = [
    {
      key: 'brand_id',
      type: 'searchable-select',
      label: 'ยี่ห้อ',
      value: filterBrandId?.toString() || '',
      onChange: (value) => {
        setFilterBrandId(value ? parseInt(value.toString()) : undefined);
        setCurrentPage(1);
      },
      options: [
        { value: '', label: 'ทั้งหมด' },
        ...activeBrands.map(brand => ({
          value: brand.id.toString(),
          label: brand.name
        })),
        ...inactiveBrands
          .filter(brand => !activeBrands.some(a => a.id === brand.id))
          .map(brand => ({
            value: brand.id.toString(),
            label: `${brand.name} (ปิดใช้งาน)`
          }))
      ],
      placeholder: 'เลือกยี่ห้อ',
      searchPlaceholder: 'ค้นหายี่ห้อ...'
    },
    {
      key: 'is_active',
      type: 'select',
      label: 'สถานะ',
      value: filterIsActive === undefined ? '' : filterIsActive ? 'true' : 'false',
      onChange: (value) => {
        setFilterIsActive(value === '' ? undefined : value === 'true');
        setCurrentPage(1);
      },
      options: [
        { value: '', label: 'ทั้งหมด' },
        { value: 'true', label: 'ใช้งาน' },
        { value: 'false', label: 'ไม่ใช้งาน' }
      ]
    }
  ];

  return (
    <Layout>
      <div className="space-y-6">
        {/* Page Header with Executive Navy Theme */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
          <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-primary-600 flex items-center justify-center flex-shrink-0 shadow-sm">
            <RiCpuLine className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-secondary-900 leading-tight">รุ่นอุปกรณ์</h1>
            <p className="text-xs text-secondary-500 mt-0.5">จัดการรุ่นของอุปกรณ์ทั้งหมด</p>
          </div>
        </div>
        {totalItems > 0 && (
          <span className="inline-flex items-center px-3 min-h-[44px] sm:min-h-[32px] py-1.5 rounded-lg bg-secondary-100 text-secondary-600 text-xs font-medium self-start sm:self-auto">
            {totalItems.toLocaleString()} รายการ
          </span>
        )}
      </div>

        {/* Error Message from React Query */}
        {error && (
          <div className="bg-danger-50 border border-danger-300 rounded-lg p-4">
            <div className="flex items-center">
              <RiErrorWarningLine className="w-5 h-5 text-danger-600 mr-2" />
              <span className="text-danger-800">เกิดข้อผิดพลาดในการโหลดข้อมูล</span>
            </div>
          </div>
        )}

        {/* Action Buttons Row */}
        <ActionButtonsRow
          onAdd={canCreate('device_models') ? () => handleOpenModal('create') : undefined}
          onRefresh={handleRefresh}
          refreshLoading={isFetching}
          onExport={canExport('device_models') ? handleExportExcel : undefined}
          exportDisabled={models.length === 0}
        />

        {/* Search & Filter Card */}
        <SearchFilterCard
          searchValue={searchTerm}
          onSearchChange={(value) => {
            setSearchTerm(value);
            setCurrentPage(1);
          }}
          searchPlaceholder="ค้นหารหัส/ชื่อรุ่นอุปกรณ์..."
          filters={filters}
          onClearFilters={() => {
            setSearchTerm('');
            setFilterBrandId(undefined);
            setFilterIsActive(undefined);
            setCurrentPage(1);
          }}
        />

        {/* Bulk Actions Bar */}
        {canDelete('device_models') && (
          <BulkActionsBar
            selectedCount={selectedModels.length}
            onClearSelection={() => setSelectedModels([])}
            onBulkDelete={handleBulkDelete}
            bulkLoading={bulkLoading}
          />
        )}

        {/* Table */}
        <Table
          columns={canDelete('device_models') ? columns : columns.filter(c => c.key !== 'select')}  /* KD3 S2122 (BUG-S2122-004): ไม่มีสิทธิ์ลบ = ไม่มีช่องติ๊ก (ติ๊กแล้วไม่มีอะไรให้ทำ) */
          data={models}
          loading={isLoading}
          emptyText={error ? 'โหลดข้อมูลไม่สำเร็จ' : 'ไม่พบข้อมูลรุ่นอุปกรณ์'}
          emptyHint={error ? null : undefined}  /* BUG-S2273-001: โหลดล้ม ≠ ตัวกรองไม่เจอ */
          pagination={false}
          searchable={false}
          actions={false}
          rowKey="id"
        />

        {/* Pagination */}
        {!isLoading && models.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalItems={totalItems}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={(newPageSize) => {
              setPageSize(newPageSize);
              setCurrentPage(1);
            }}
            showPageSizeSelector={true}
            showSummary={true}
            className="mt-5"
          />
        )}

        {/* Modal */}
        <Modal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          title={
            modalMode === 'create' ? 'เพิ่มรุ่นอุปกรณ์' :
            modalMode === 'edit' ? 'แก้ไขรุ่นอุปกรณ์' : 'ดูรายละเอียดรุ่นอุปกรณ์'
          }
          size="lg"
          icon={<RiCpuLine className="h-6 w-6 text-primary-600" />}
        >
          <div className="space-y-6">
            {/* Section: ข้อมูลพื้นฐาน */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 pb-2 border-b border-secondary-200">
                <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg flex items-center justify-center shadow-sm">
                  <RiInformationLine className="w-4 h-4 text-white" />
                </div>
                <h3 className="text-base font-semibold text-secondary-800">ข้อมูลพื้นฐาน</h3>
              </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* รหัสรุ่น - readonly ในทุกโหมด */}
              <div>
                <ValidatedInput
                  label="รหัสรุ่น"
                  name="code"
                  value={formData.code}
                  onChange={handleFormChange}
                  error={formErrors.code}
                  required={true}
                  disabled={true}
                  placeholder="รหัสจะสร้างอัตโนมัติ"
                />
                {codeError && (
                  <p className="mt-1 text-sm text-danger-600 flex items-center">
                    <RiErrorWarningLine className="w-4 h-4 mr-1" />
                    {codeError}
                  </p>
                )}
              </div>

              {/* ยี่ห้อ - required, ต้องเลือกก่อน */}
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">
                  ยี่ห้อ <span className="text-danger-500">*</span>
                </label>
                <select
                  aria-label="ยี่ห้อ"
                  name="brand_id"
                  value={formData.brand_id || ''}
                  onChange={handleFormChange}
                  disabled={(modalMode as string) === 'view'}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 ${
                    formErrors.brand_id ? 'border-danger-500' : 'border-secondary-300'
                  } ${modalMode === 'view' ? 'bg-secondary-100' : ''}`}
                  aria-invalid={Boolean(formErrors.brand_id)}
                  aria-describedby={formErrors.brand_id ? 'brand_id-error' : undefined}
                >
                  <option value="">-- เลือกยี่ห้อ --</option>
                  {withCurrentOption(activeBrands, modalMode === 'create' ? null : selectedModel?.brand).map((brand) => (
                    <option key={brand.id} value={brand.id}>
                      {brand.name}
                    </option>
                  ))}
                </select>
                {formErrors.brand_id && (
                  <p id="brand_id-error" className="mt-1 text-sm text-danger-600">{formErrors.brand_id}</p>
                )}
              </div>
            </div>

            {/* ชื่อรุ่น */}
            <ValidatedInput
              label="ชื่อรุ่น"
              name="name"
              value={formData.name}
              onChange={handleFormChange}
              error={formErrors.name}
              required={true}
              disabled={(modalMode as string) === 'view'}
              placeholder="เช่น V2 Pro, G2"
            />

            <ValidatedTextarea
              label="คำอธิบาย"
              name="description"
              value={formData.description}
              onChange={handleFormChange}
              disabled={(modalMode as string) === 'view'}
              rows={3}
              placeholder="คำอธิบายเพิ่มเติมเกี่ยวกับรุ่นอุปกรณ์"
            />

            {/* ฟิลด์สถานะ */}
            {modalMode !== 'view' && (
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">
                  สถานะ
                </label>
                <select
                  aria-label="สถานะ"
                  name="is_active"
                  value={formData.is_active ? 'true' : 'false'}
                  onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.value === 'true' }))}
                  className="w-full px-3 py-2 border border-secondary-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500"
                  disabled={(modalMode as string) === 'view'}
                >
                  <option value="true">ใช้งาน</option>
                  <option value="false">ไม่ใช้งาน</option>
                </select>
              </div>
            )}
            </div>

            {modalMode === 'view' && selectedModel && (
              <>
                {/* Section: ข้อมูลสถานะ */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2 pb-2 border-b border-secondary-200">
                    <div className="w-8 h-8 bg-gradient-to-br from-success-500 to-success-600 rounded-lg flex items-center justify-center shadow-sm">
                      <RiInformationLine className="w-4 h-4 text-white" />
                    </div>
                    <h3 className="text-base font-semibold text-secondary-800">สถานะ</h3>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-secondary-600">สถานะ:</span>
                      <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${
                        selectedModel.is_active
                          ? 'bg-success-100 text-success-800'
                          : 'bg-danger-100 text-danger-800'
                      }`}>
                        {selectedModel.is_active ? 'ใช้งาน' : 'ไม่ใช้งาน'}
                      </span>
                    </div>
                    <div>
                      <span className="text-secondary-600">วันที่สร้าง:</span>
                      <span className="ml-2">{formatDateTimeFull(selectedModel.created_at)}</span>
                    </div>
                  </div>
                </div>

                {/* Section: ข้อมูลการสร้างและแก้ไข */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2 pb-2 border-b border-secondary-200">
                    <div className="w-8 h-8 bg-gradient-to-br from-info-500 to-info-600 rounded-lg flex items-center justify-center shadow-sm">
                      <RiHistoryLine className="w-4 h-4 text-white" />
                    </div>
                    <h3 className="text-base font-semibold text-secondary-800">ข้อมูลการสร้างและแก้ไข</h3>
                  </div>
                  <div className="p-4 bg-gradient-to-r from-secondary-50 to-secondary-100/50 rounded-xl border border-secondary-200/50">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-secondary-600">สร้างโดย:</span>
                        <span className="ml-2 font-medium">
                          {selectedModel.created_by?.full_name || selectedModel.created_by?.username || '-'}
                        </span>
                      </div>
                      <div>
                        <span className="text-secondary-600">วันที่สร้าง:</span>
                        <span className="ml-2 font-medium">
                          {formatDateTimeFull(selectedModel.created_at)}
                        </span>
                      </div>
                      {/* แสดงข้อมูลการแก้ไขเฉพาะเมื่อเคยถูกแก้ไขจริง */}
                      {selectedModel.updated_at && selectedModel.updated_by && (
                        <>
                          <div>
                            <span className="text-secondary-600">แก้ไขล่าสุดโดย:</span>
                            <span className="ml-2 font-medium">
                              {selectedModel.updated_by?.full_name || selectedModel.updated_by?.username || '-'}
                            </span>
                          </div>
                          <div>
                            <span className="text-secondary-600">วันที่แก้ไขล่าสุด:</span>
                            <span className="ml-2 font-medium">
                              {formatDateTimeFull(selectedModel.updated_at)}
                            </span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="flex flex-wrap justify-end gap-3 mt-6">
            <button
              onClick={handleCloseModal}
              className="px-4 py-2 text-secondary-700 bg-secondary-200 rounded-lg hover:bg-secondary-300"
            >
              {modalMode === 'view' ? 'ปิด' : 'ยกเลิก'}
            </button>
            {modalMode !== 'view' && (
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
              >
                {isSaving ? 'กำลังบันทึก...' : 'บันทึก'}
              </button>
            )}
          </div>
        </Modal>

        {/* Loading Overlay for Export */}
        <LoadingOverlayModal
          isOpen={isExporting}
          icon="export"
          title="กำลังส่งออกข้อมูล"
          submessage="กรุณารอสักครู่ ระบบกำลังเตรียมไฟล์ Excel"
        />
      </div>
    </Layout>
  );
};

export default DeviceModelPage;
